import { useEffect, useMemo, useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { Award, Sparkles } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { toast } from 'sonner';
import {
  BURN_RULE_LABELS,
  canRedeemLoyaltyAtPos,
  inferLoyaltyBurnPurposeFromBillItems,
  LOYALTY_REDEEM_HOLD_MESSAGE,
  loyaltyApi,
  type LoyaltyBurnPurpose,
  type PatientLoyaltySummary,
} from '@/api/loyalty';
import { billingApi } from '@/api/billing';
import { Bill } from '@/types/types';
import { useAuth } from '@/contexts/AuthContext';

type Props = {
  bill: Bill;
  onApplied?: () => void;
  purposeOverride?: LoyaltyBurnPurpose;
  patientId?: string;
};

function formatRedeemableAt(iso: string | null | undefined) {
  if (!iso) return null;
  return new Date(iso).toLocaleString(undefined, {
    day: 'numeric',
    month: 'short',
    hour: '2-digit',
    minute: '2-digit',
  });
}

export default function LoyaltyBillingPanel({
  bill,
  onApplied,
  purposeOverride,
  patientId: patientIdOverride,
}: Props) {
  const { user } = useAuth();
  const queryClient = useQueryClient();
  const patientId =
    patientIdOverride ?? bill.patient?.id ?? (bill as { patientId?: string }).patientId;
  const purpose = useMemo(
    () => purposeOverride ?? inferLoyaltyBurnPurposeFromBillItems(bill.billItems),
    [bill.billItems, purposeOverride]
  );
  const dueAmount = bill.dueAmount ?? 0;
  const canRedeem = canRedeemLoyaltyAtPos(user?.role);

  const [pointsToRedeem, setPointsToRedeem] = useState('');

  const { data: summary, isLoading } = useQuery({
    queryKey: ['loyalty-summary', patientId],
    queryFn: () => loyaltyApi.getPatientSummary(patientId!),
    enabled: !!patientId && dueAmount > 0 && canRedeem,
  });

  const { data: quote } = useQuery({
    queryKey: ['loyalty-quote', patientId, purpose, dueAmount],
    queryFn: () => loyaltyApi.getRedemptionQuote(patientId!, purpose, dueAmount),
    enabled: !!patientId && !!summary?.loyaltyEnabled && dueAmount > 0 && canRedeem,
  });

  useEffect(() => {
    if (quote?.maxPoints) {
      setPointsToRedeem(String(quote.maxPoints));
    }
  }, [quote?.maxPoints, dueAmount]);

  const applyMutation = useMutation({
    mutationFn: async () => {
      const pts = Math.floor(Number(pointsToRedeem));
      if (!pts || pts <= 0) throw new Error('Enter points to redeem');
      return billingApi.applyLoyaltyDiscount(bill.id, { points: pts, purpose });
    },
    onSuccess: (res) => {
      const data = res.data?.data;
      toast.success(
        `Applied ₹${data?.rupeeValue ?? ''} loyalty discount (${data?.pointsBurned ?? ''} pts) — bill updated`
      );
      queryClient.invalidateQueries({ queryKey: ['bill', bill.id] });
      queryClient.invalidateQueries({ queryKey: ['loyalty-summary', patientId] });
      queryClient.invalidateQueries({ queryKey: ['loyalty-quote', patientId] });
      onApplied?.();
    },
    onError: (err: { response?: { data?: { message?: string } }; message?: string }) => {
      toast.error(err.response?.data?.message || err.message || 'Loyalty redemption failed');
    },
  });

  if (!patientId || dueAmount <= 0 || !canRedeem) return null;

  if (isLoading) {
    return (
      <div className="rounded-xl border border-violet-200 bg-violet-50/50 p-4 text-sm text-violet-800">
        Loading loyalty balance…
      </div>
    );
  }

  const s = summary as PatientLoyaltySummary | undefined;
  if (!s?.loyaltyEnabled) return null;

  const balance = s.pointsBalance ?? 0;
  const redeemable = s.redeemableBalance ?? quote?.redeemableBalance ?? balance;
  const onHold = s.pointsOnHold ?? quote?.pointsOnHold ?? 0;
  const holdMessage = s.redeemHoldMessage ?? quote?.redeemHoldMessage ?? LOYALTY_REDEEM_HOLD_MESSAGE;
  const nextRedeemableAt = formatRedeemableAt(s.nextRedeemableAt ?? quote?.nextRedeemableAt);
  const maxPts = quote?.maxPoints ?? 0;
  const maxRupees = quote?.maxRupees ?? 0;
  const ppr = s.pointsPerRupee ?? 100;
  const ptsNum = Math.floor(Number(pointsToRedeem) || 0);
  const estimatedOff =
    ptsNum > 0 && ppr > 0 ? Math.min(ptsNum / ppr, dueAmount) : 0;

  return (
    <div className="rounded-xl border border-violet-200 bg-gradient-to-br from-violet-50 to-amber-50/40 p-4 space-y-3">
      <div className="flex items-start gap-3">
        <span className="flex h-10 w-10 shrink-0 items-center justify-center rounded-full bg-violet-600 text-white">
          <Award className="h-5 w-5" />
        </span>
        <div className="min-w-0 flex-1">
          <p className="font-semibold text-violet-950 flex items-center gap-1.5">
            Redeem loyalty points (POS)
            <Sparkles className="h-4 w-4 text-amber-500" />
          </p>
          <p className="text-sm text-violet-800 mt-0.5">
            {bill.patient?.name ?? 'Patient'} has{' '}
            <strong>{redeemable.toLocaleString()}</strong> redeemable points
            {onHold > 0 ? (
              <>
                {' '}
                ({onHold.toLocaleString()} on hold · total {balance.toLocaleString()})
              </>
            ) : null}
            {s.membership?.displayName ? ` · ${s.membership.displayName} member` : ''}
            {s.familyPoolingEnabled && s.ownPointsBalance != null
              ? ` (family pool; own wallet: ${(s.ownPointsBalance ?? 0).toLocaleString()})`
              : ''}
          </p>
          <p className="text-xs text-violet-700 mt-1">
            Ask how many points they want to use, apply the discount here, then collect the reduced
            balance in cash/UPI/card.
          </p>
        </div>
      </div>

      <p className="text-xs text-amber-900 rounded-md border border-amber-200 bg-amber-50/80 px-3 py-2">
        {holdMessage}
        {nextRedeemableAt ? ` Next points unlock at ${nextRedeemableAt}.` : ''}
      </p>

      {redeemable > 0 && maxPts > 0 ? (
        <>
          <p className="text-xs text-violet-700">
            On this bill ({BURN_RULE_LABELS[purpose]}): up to{' '}
            <strong>{maxPts.toLocaleString()}</strong> pts (₹{maxRupees.toFixed(2)} max) · due now ₹
            {dueAmount.toFixed(2)}
          </p>
          <div className="flex flex-wrap items-end gap-2">
            <div className="flex-1 min-w-[120px]">
              <Label htmlFor="loyaltyPointsRedeem" className="text-xs text-violet-800">
                Points to redeem
              </Label>
              <Input
                id="loyaltyPointsRedeem"
                type="number"
                min={1}
                max={maxPts}
                value={pointsToRedeem}
                onChange={(e) => setPointsToRedeem(e.target.value)}
                className="mt-1 bg-white"
              />
            </div>
            <Button
              type="button"
              variant="outline"
              className="border-violet-300 text-violet-900"
              disabled={maxPts <= 0}
              onClick={() => setPointsToRedeem(String(maxPts))}
            >
              Use max ({maxPts.toLocaleString()})
            </Button>
            <Button
              type="button"
              variant="secondary"
              className="bg-violet-700 text-white hover:bg-violet-800"
              disabled={applyMutation.isPending || ptsNum <= 0 || ptsNum > maxPts}
              onClick={() => applyMutation.mutate()}
            >
              {applyMutation.isPending ? 'Applying…' : 'Apply to bill'}
            </Button>
          </div>
          {ptsNum > 0 && (
            <p className="text-xs text-slate-600">
              Bill will reduce by ≈ ₹{estimatedOff.toFixed(2)} (before payment).
            </p>
          )}
        </>
      ) : (
        <p className="text-xs text-violet-700">
          {onHold > 0 && redeemable <= 0
            ? 'Points are on hold until 24 hours after they were credited.'
            : 'No points available to redeem on this bill amount, or redemption type is disabled.'}
        </p>
      )}
    </div>
  );
}
